// Session keep-alive scheduling — pure math (no React, no I/O).
//
// apiv2 is the authority on how long a session lives: `session.extend` answers
// with the TTL it actually granted (cohort `inactive_time_log_off` + the 120 s
// min-interval ≈ 32 min for an RPM coordinator; a 300 s floor when the caller
// sends no `ttl`). Every delay here is derived from that number, so the client
// self-adapts to any cohort setting instead of assuming a fixed lifetime.

import {
  SESSION_EXTEND_LEAD_MS,
  SESSION_EXTEND_MIN_DELAY_MS,
  SESSION_EXTEND_FALLBACK_MS,
} from '@/config/constants';

/** Whether the server handed back a TTL we can schedule against. */
export function isUsableTtl(grantedTtlSeconds: unknown): boolean {
  const ttl = Number(grantedTtlSeconds);
  return Number.isFinite(ttl) && ttl > 0;
}

/**
 * Delay until the next `session.extend`, from the TTL the server granted.
 *
 * The lead is capped at half the grant so a short TTL still schedules in the
 * future, and the result never dips below the min-interval floor.
 */
export function nextExtendDelayMs(grantedTtlSeconds: unknown): number {
  if (!isUsableTtl(grantedTtlSeconds)) return SESSION_EXTEND_FALLBACK_MS;
  const ttlMs = Number(grantedTtlSeconds) * 1000;
  const lead = Math.min(SESSION_EXTEND_LEAD_MS, ttlMs / 2);
  return Math.max(SESSION_EXTEND_MIN_DELAY_MS, ttlMs - lead);
}
